import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { bookingApi } from '../../../utils/bookingApi';
import CalendarNav from '../CalendarNav';
import { ClipboardDocumentListIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { ConsentIcons, EmergencyButton, MedicalNotes, SessionActionsMenu } from './sessionPanel';
import {
  PageHeader, Sheet, Picker, Skeleton, ErrorState, InlineError, useConfirm, useToast,
} from '../../../components/ui';
import { formatDate, formatDateTime, formatDayDate } from '../../../utils/dates';
import ChoiceSheet from './ChoiceSheet';
import '../booking-shared.css';
import '../BookingCalendar.css';
import SessionTypePill from '../../../components/sessions/SessionTypePill';

// ─── ManualAddForm ───────────────────────────────────────────────────────────

const fullName = (p) => `${p.firstName} ${p.lastName}`;

// Adding gymnasts to a session by hand, in a pop-up
function ManualAddForm({ sessionId, bookedGymnastIds, onAdded, onClose }) {
  const toast = useToast();
  const [allGymnasts, setAllGymnasts] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [selected, setSelected] = useState([]); // gymnast ids
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoadFailed(false);
    const API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api`;
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    fetch(`${API_URL}/gymnasts`, { headers })
      .then(r => r.json())
      .then(g => setAllGymnasts(Array.isArray(g) ? g : g.gymnasts || []))
      .catch((err) => { console.error(err); setLoadFailed(true); });
  }, []);
  useEffect(() => { load(); }, [load]);

  // Gymnasts who can be added: not archived and not already booked
  const items = (allGymnasts || [])
    .filter(g => !g.isArchived && !bookedGymnastIds.includes(g.id))
    .map(g => ({
      id: g.id,
      label: fullName(g),
      detail: g.guardians?.length ? `Family: ${g.guardians.map(fullName).join(', ')}` : undefined,
      search: (g.guardians || []).map(gu => `${fullName(gu)} ${gu.email || ''}`).join(' '),
    }));

  // Derive the booking userId for a gymnast: own account first, then first guardian
  const getAccountHolder = (g) =>
    g.userId || g.guardians?.[0]?.id || null;

  const handleSubmit = async () => {
    if (selected.length === 0) {
      setError('Choose at least one gymnast.');
      return;
    }
    setSubmitting(true);
    setError(null);

    // Group gymnasts by account holder
    const groups = {};
    for (const id of selected) {
      const g = allGymnasts.find(x => x.id === id);
      const uid = getAccountHolder(g);
      if (!uid) {
        setError(`${fullName(g)} has no account yet. Ask their family to create one first.`);
        setSubmitting(false);
        return;
      }
      if (!groups[uid]) groups[uid] = [];
      groups[uid].push(g.id);
    }

    try {
      for (const [userId, gymnastIds] of Object.entries(groups)) {
        await bookingApi.adminAddToSession({ sessionInstanceId: sessionId, gymnastIds, userId });
      }
      toast(selected.length === 1 ? 'Gymnast added' : `${selected.length} gymnasts added`);
      setSelected([]);
      onAdded();
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't add them to the session.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet
      title="Add participant"
      onClose={onClose}
      dirty={selected.length > 0}
      onSubmit={handleSubmit}
      footer={(requestClose) => (
        <div className="bk-actions">
          <button type="submit" disabled={submitting || selected.length === 0} className="bk-btn bk-btn--primary">
            {submitting ? 'Adding…' : 'Add'}
          </button>
          <button type="button" className="bk-btn" onClick={requestClose}>Cancel</button>
        </div>
      )}
    >
      {loadFailed ? (
        <InlineError onRetry={load}>Couldn't load the gymnasts.</InlineError>
      ) : allGymnasts === null ? (
        <Skeleton rows={2} label="Loading gymnasts…" />
      ) : (
        <>
          <label className="bk-label" htmlFor="manual-add-gymnasts">Gymnasts</label>
          <Picker id="manual-add-gymnasts" label="Gymnasts" items={items} value={selected} onChange={setSelected} multiple
            placeholder="Search by gymnast or family name…" />
        </>
      )}
      {error && <p className="bk-form-error" role="alert">{error}</p>}
    </Sheet>
  );
}

// Sharing the register with a cover coach, in a pop-up
function ShareRegister({ selectedSession, onClose }) {
  const confirm = useConfirm();
  const [shareHours, setShareHours] = useState(72);
  const [shareTokens, setShareTokens] = useState([]);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState(null);
  const [shareCopied, setShareCopied] = useState(null);

  const loadShareTokens = useCallback(() => {
    bookingApi.listRegisterTokens(selectedSession)
      .then(res => setShareTokens(res.data))
      .catch(() => { setShareTokens([]); setShareError("Couldn't load the links already shared."); });
  }, [selectedSession]);

  useEffect(() => { loadShareTokens(); }, [loadShareTokens]);

  const handleCreateShare = async () => {
    setShareLoading(true);
    setShareError(null);
    try {
      const res = await bookingApi.createRegisterToken(selectedSession, shareHours);
      setShareCopied(null);
      // Try to auto-copy the URL
      try { await navigator.clipboard.writeText(res.data.url); setShareCopied(res.data.id); }
      catch { /* clipboard might be blocked: the link shows below to copy */ }
      loadShareTokens();
    } catch (err) {
      setShareError(err.response?.data?.error || "Couldn't make a link.");
    } finally {
      setShareLoading(false);
    }
  };

  const handleCopyShare = async (t) => {
    try { await navigator.clipboard.writeText(t.url); setShareCopied(t.id); }
    catch { setShareError("Couldn't copy it. Select the link and copy it yourself."); }
  };

  const handleRevokeShare = async (tokenId) => {
    if (!(await confirm({
      title: 'Revoke this share link?',
      message: 'Anyone holding it will no longer be able to open the register.',
      confirmLabel: 'Revoke',
    }))) return;
    try {
      await bookingApi.revokeRegisterToken(tokenId);
      loadShareTokens();
    } catch (err) {
      setShareError(err.response?.data?.error || "Couldn't revoke the link.");
    }
  };

  return (
    <Sheet
      title="Share register"
      onClose={onClose}
      footer={(
        <div className="bk-actions">
          <button type="button" className="bk-btn" onClick={onClose}>Close</button>
        </div>
      )}
    >
      <p className="ui-sheet__message">
        Makes a link the cover coach can open without logging in. It shows the full register, skills in progress, health notes and emergency contacts. Don't share it beyond the cover coach.
      </p>
      <label className="bk-label">Expires in
        <select className="bk-input" value={shareHours} onChange={e => setShareHours(Number(e.target.value))}>
          <option value={24}>24 hours</option>
          <option value={72}>3 days</option>
          <option value={168}>7 days</option>
        </select>
      </label>
      <button type="button" className="bk-btn bk-btn--primary bk-btn--full" onClick={handleCreateShare} disabled={shareLoading}>
        {shareLoading ? 'Making link…' : 'Make link'}
      </button>
      {shareError && <p className="bk-form-error" role="alert">{shareError}</p>}
      {shareTokens.length > 0 && (
        <div className="sp-share-links">
          <h3 className="bk-section-title">Active links</h3>
          {shareTokens.map(t => (
            <div key={t.id} className="sp-share-link">
              <input readOnly value={t.url} className="bk-input" aria-label="Share link" onFocus={e => e.target.select()} />
              <p className="bk-small">
                Expires {formatDateTime(t.expiresAt)} · Views: {t.viewCount}{t.lastViewedAt ? ` (last ${formatDateTime(t.lastViewedAt)})` : ''}
              </p>
              <div className="bk-row-actions">
                <button type="button" className="bk-btn bk-btn--sm" onClick={() => handleCopyShare(t)}>
                  {shareCopied === t.id ? 'Copied' : 'Copy link'}
                </button>
                <button type="button" className="bk-btn bk-btn--sm bk-btn--outline-danger" onClick={() => handleRevokeShare(t.id)}>
                  Revoke
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Sheet>
  );
}

// ─── SessionDetailPanel ──────────────────────────────────────────────────────

export function SessionDetailPanel({ sessionDetail, selectedSession, showManualAdd, setShowManualAdd, onAdded }) {
  const confirm = useConfirm();
  const toast = useToast();
  const [confirmingRemove, setConfirmingRemove] = useState(null); // { bookingId, line }
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState(null);
  const [standingSlots, setStandingSlots] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsFailed, setSlotsFailed] = useState(false);
  const [absentGymnastIds, setAbsentGymnastIds] = useState([]);
  const [cancelError, setCancelError] = useState(null);
  const [showShareForm, setShowShareForm] = useState(false);
  const totalGymnasts = sessionDetail.bookings?.reduce(
    (n, b) => n + b.lines.filter(l => !l.cancelledAt).length, 0,
  ) ?? 0;
  const capacity = sessionDetail.capacity;

  const loadSlots = useCallback(() => {
    if (!sessionDetail.templateId) return;
    setSlotsLoading(true);
    setSlotsFailed(false);
    Promise.all([
      bookingApi.getCommitmentsForTemplate(sessionDetail.templateId),
      bookingApi.getAttendance(selectedSession),
    ]).then(([commitmentsRes, attendanceRes]) => {
      setStandingSlots(commitmentsRes.data);
      setAbsentGymnastIds(
        attendanceRes.data.attendees
          .filter(a => a.status === 'ABSENT')
          .map(a => a.gymnastId)
      );
    }).catch(() => { setStandingSlots(null); setAbsentGymnastIds([]); setSlotsFailed(true); })
      .finally(() => setSlotsLoading(false));
  }, [sessionDetail.templateId, selectedSession]);
  useEffect(() => { loadSlots(); }, [loadSlots]);

  const handleCancelSession = async () => {
    const reason = await confirm({
      title: 'Cancel this session?',
      message: 'Every booking is refunded as a credit, and affected parents and waitlisted families are emailed.',
      confirmLabel: 'Cancel session',
      cancelLabel: 'Keep it',
      input: { label: 'Reason (shown to parents)', placeholder: 'For example, coach unwell', required: true, multiline: true },
    });
    if (!reason) return;
    setCancelError(null);
    try {
      await bookingApi.cancelSession(selectedSession, reason);
      toast('Session cancelled');
      onAdded();
    } catch (err) {
      setCancelError(err.response?.data?.error || "Couldn't cancel the session.");
    }
  };

  // Removes a single gymnast from a booking. Other gymnasts on the same
  // booking remain confirmed. (Previously this called cancelBooking, which
  // cancelled the whole booking, taking off other gymnasts the admin
  // didn't intend to touch.)
  const handleRemove = async (issueCredit) => {
    const { bookingId, line } = confirmingRemove;
    setRemoving(true);
    setRemoveError(null);
    try {
      await bookingApi.cancelBookingLine(bookingId, line.id, { issueCredit });
      setConfirmingRemove(null);
      toast(`${line.gymnast.firstName} removed`);
      onAdded();
    } catch (err) {
      setRemoveError(err.response?.data?.error || "Couldn't remove them.");
      setConfirmingRemove(null);
    } finally {
      setRemoving(false);
    }
  };

  const booked = totalGymnasts + (sessionDetail.activeCommitments ?? 0);
  const menuItems = [
    { label: 'Add participant', onSelect: () => setShowManualAdd(true) },
    ...(!sessionDetail.cancelledAt ? [
      { label: 'Share register', onSelect: () => setShowShareForm(true) },
      { label: 'Cancel session', onSelect: handleCancelSession, danger: true },
    ] : []),
  ];

  return (
    <div>
      <div className="sp-header">
        <SessionActionsMenu items={menuItems} />
        <p className="sp-header__time">{sessionDetail.startTime} to {sessionDetail.endTime}</p>
        <div className="sp-header__counts">
          <span>{totalGymnasts} booked</span>
          <span>
            {capacity - booked} remaining
            {sessionDetail.activeCommitments > 0 && ` (${sessionDetail.activeCommitments} standing)`}
          </span>
        </div>
        <div className="sp-header__bar">
          <div
            className={`sp-header__fill${booked >= capacity ? ' sp-header__fill--full' : ''}`}
            style={{ width: `${Math.min(100, (booked / capacity) * 100)}%` }}
          />
        </div>
        {sessionDetail.cancelledAt && <p className="sp-header__cancelled"><span className="bk-pill bk-pill--bad">Cancelled</span></p>}
      </div>

      {cancelError && <p className="bk-form-error" role="alert">{cancelError}</p>}

      <div className="sp-tiles">
        <Link className="sp-tile sp-tile--primary" to={`/booking/admin/register/${selectedSession}`}>
          <ClipboardDocumentListIcon aria-hidden="true" />
          Open register
        </Link>
        <Link className="sp-tile" to={`/gymnasts?session=${selectedSession}`}>
          <ChartBarIcon aria-hidden="true" />
          Track these gymnasts
        </Link>
      </div>

      {showManualAdd && (
        <ManualAddForm
          sessionId={selectedSession}
          bookedGymnastIds={sessionDetail.bookings?.flatMap(b => b.lines.filter(l => !l.cancelledAt).map(l => l.gymnast.id)) ?? []}
          onAdded={onAdded}
          onClose={() => setShowManualAdd(false)}
        />
      )}

      {showShareForm && !sessionDetail.cancelledAt && (
        <ShareRegister selectedSession={selectedSession} onClose={() => setShowShareForm(false)} />
      )}

      {sessionDetail.templateId && (
        <div className="bk-card sp-section">
          <h3 className="sp-section__title">Standing slots</h3>
          {slotsLoading && <Skeleton rows={2} label="Loading standing slots…" />}
          {slotsFailed && <InlineError onRetry={loadSlots}>Couldn't load the standing slots.</InlineError>}
          {!slotsLoading && standingSlots && standingSlots.length === 0 && (
            <p className="bk-muted" style={{ margin: 0 }}>No standing slots.</p>
          )}
          {!slotsLoading && standingSlots && standingSlots.filter(c => c.status !== 'WAITLISTED').map(c => {
            const isFuture = c.status === 'ACTIVE' && c.startDate && new Date(c.startDate) > new Date();
            const isAbsent = absentGymnastIds.includes(c.gymnast.id);
            const contacts = [
              ...(c.gymnast.emergencyContactName ? [{
                role: 'Emergency contact',
                name: c.gymnast.emergencyContactName,
                relationship: c.gymnast.emergencyContactRelationship,
                phone: c.gymnast.emergencyContactPhone,
              }] : []),
              ...(!c.gymnast.userId && c.gymnast.guardians?.[0] ? [{
                role: 'Parent',
                name: `${c.gymnast.guardians[0].firstName} ${c.gymnast.guardians[0].lastName}`,
                phone: c.gymnast.guardians[0].phone,
              }] : []),
            ];
            return (
              <div key={c.id} className={`sp-participant${isAbsent ? ' sp-participant--absent' : ''}`}>
                <div className="sp-participant__top">
                  <span className="sp-participant__name">
                    {c.gymnast.firstName} {c.gymnast.lastName}
                  </span>
                  <div className="sp-participant__badges">
                    <ConsentIcons consents={c.gymnast.consents} />
                    {isAbsent && <span className="bk-pill bk-pill--off">Absent</span>}
                    {isFuture ? (
                      <span className="bk-pill bk-pill--info">Starts {formatDate(c.startDate)}</span>
                    ) : c.status === 'PAUSED' ? (
                      <span className="bk-pill bk-pill--off">Paused</span>
                    ) : null}
                  </div>
                </div>
                <div className="sp-participant__footer">
                  <span className="sp-participant__actions">
                    <MedicalNotes notes={c.gymnast.healthNotes} />
                    <EmergencyButton contacts={contacts} />
                  </span>
                </div>
              </div>
            );
          })}
          {!slotsLoading && standingSlots && standingSlots.some(c => c.status === 'WAITLISTED') && (
            <>
              <h4 className="sp-section__subtitle">Waiting for a place</h4>
              {standingSlots
                .filter(c => c.status === 'WAITLISTED')
                .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                .map((c, i) => (
                  <div key={c.id} className="sp-participant">
                    <div className="sp-participant__top">
                      <span className="sp-participant__name">{i + 1}. {c.gymnast.firstName} {c.gymnast.lastName}</span>
                      <span className="bk-small">since {formatDate(c.createdAt)}</span>
                    </div>
                  </div>
                ))}
            </>
          )}
        </div>
      )}

      <div className="bk-card sp-section">
        <h3 className="sp-section__title">Attendees</h3>
        {totalGymnasts === 0 && <p className="bk-muted" style={{ margin: 0 }}>No bookings yet.</p>}
        {removeError && <p className="bk-form-error" role="alert">{removeError}</p>}
        {sessionDetail.bookings?.map(b =>
          b.lines.filter(l => !l.cancelledAt).map(l => {
            const contacts = [
              ...(l.gymnast.emergencyContactName ? [{
                role: 'Emergency contact',
                name: l.gymnast.emergencyContactName,
                relationship: l.gymnast.emergencyContactRelationship,
                phone: l.gymnast.emergencyContactPhone,
              }] : []),
              ...(l.gymnast.userId !== b.user.id ? [{
                role: 'Booked by',
                name: `${b.user.firstName} ${b.user.lastName}`,
                phone: b.user.phone,
              }] : []),
            ];
            return (
            <div key={l.id} className="sp-participant">
              <div className="sp-participant__top">
                <span className="sp-participant__name">{l.gymnast.firstName} {l.gymnast.lastName}</span>
                <div className="sp-participant__badges">
                  <ConsentIcons consents={l.gymnast.consents} />
                </div>
              </div>
              <div className="sp-participant__footer">
                <button
                  type="button"
                  className="bk-btn bk-btn--sm bk-btn--outline-danger"
                  onClick={() => { setConfirmingRemove({ bookingId: b.id, line: l }); setRemoveError(null); }}
                >
                  Remove
                </button>
                <span className="sp-participant__actions">
                  <MedicalNotes notes={l.gymnast.healthNotes} />
                  <EmergencyButton contacts={contacts} />
                </span>
              </div>
            </div>
            );
          })
        )}
      </div>

      {confirmingRemove && (
        <ChoiceSheet
          danger
          busy={removing}
          title={`Remove ${confirmingRemove.line.gymnast.firstName} from this session?`}
          message="Give their family a credit for the place, or remove them with no credit."
          yes={removing ? 'Removing…' : 'With credit'}
          no="No credit"
          onYes={() => handleRemove(true)}
          onNo={() => handleRemove(false)}
          onCancel={() => setConfirmingRemove(null)}
        />
      )}
    </div>
  );
}

// ─── BookingAdmin ─────────────────────────────────────────────────────────────

// /booking/admin: the sessions calendar for staff. Choosing a session opens
// its details in a pop-up.
export default function BookingAdmin() {
  const location = useLocation();
  const lastNavigatedDate = React.useRef(new Date());
  const preselectRef = useRef(location.state?.preselect || null);
  const [sessions, setSessions] = useState([]);
  const [closures, setClosures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [detailFailed, setDetailFailed] = useState(false);
  const [showManualAdd, setShowManualAdd] = useState(false);

  // Reload the panel data for the currently-selected session. Guarded with
  // an effect-scoped `cancelled` flag so that if the user clicks a different
  // session before the previous fetch returns, the stale response is
  // discarded; otherwise the panel can end up showing the wrong session.
  const loadDetail = (id) =>
    bookingApi.getSession(id).then(res => setSessionDetail(res.data)).catch(() => setDetailFailed(true));

  const [detailTry, setDetailTry] = useState(0);
  useEffect(() => {
    if (!selectedSession) return undefined;
    let cancelled = false;
    setDetailFailed(false);
    bookingApi.getSession(selectedSession).then(res => {
      if (!cancelled) setSessionDetail(res.data);
    }).catch(() => { if (!cancelled) setDetailFailed(true); });
    return () => { cancelled = true; };
  }, [selectedSession, detailTry]);

  const getClosureForDate = (date) => {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return closures.find(c => {
      const start = new Date(c.startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(c.endDate);
      end.setHours(23, 59, 59, 999);
      return d >= start && d <= end;
    }) || null;
  };

  const handleNavigate = (date) => {
    lastNavigatedDate.current = date;
    setSelectedSession(null);
    setSessionDetail(null);
    setShowManualAdd(false);
    setLoading(true);
    setLoadFailed(false);
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    // Fetch both months if week spans a boundary
    const ws = new Date(date);
    ws.setDate(ws.getDate() - ws.getDay());
    ws.setHours(0, 0, 0, 0);
    const we = new Date(ws);
    we.setDate(we.getDate() + 6);
    const fetchMonths = [{ y, m }];
    if (we.getMonth() + 1 !== m || we.getFullYear() !== y) {
      fetchMonths.push({ y: we.getFullYear(), m: we.getMonth() + 1 });
    }
    Promise.all([
      ...fetchMonths.map(({ y: fy, m: fm }) => bookingApi.getSessions(fy, fm)),
      bookingApi.getClosures(),
    ]).then(results => {
      setSessions(results.slice(0, -1).flatMap(r => r.data));
      setClosures(results[results.length - 1].data);
      if (preselectRef.current) {
        setSelectedSession(preselectRef.current);
        preselectRef.current = null;
      }
    }).catch((err) => { console.error(err); setLoadFailed(true); })
      .finally(() => setLoading(false));
  };

  const handleSelect = (id) => {
    setSelectedSession(prev => prev === id ? null : id);
    setSessionDetail(null);
    setDetailFailed(false);
    setShowManualAdd(false);
  };

  const closeDetail = () => { setSelectedSession(null); setSessionDetail(null); setShowManualAdd(false); };

  const sessionDotClass = (s) =>
    s.availableSlots === 0 || s.cancelledAt ? 'full' : 'open';

  return (
    <div className="bk-page bk-page--wide">
      <PageHeader back={{ to: '/admin-hub', label: 'Admin' }} title="Sessions" />

      {loadFailed && (
        <InlineError onRetry={() => handleNavigate(lastNavigatedDate.current)}>Couldn't load the sessions.</InlineError>
      )}

      <CalendarNav
        sessions={sessions}
        onNavigate={handleNavigate}
        loading={loading}
        closures={closures}
        renderDayDots={(date, daySessions, isPast, isClosed) => {
          if (isClosed) return null;
          return daySessions.slice(0, 3).map((s, i) => (
            <span
              key={i}
              className={`booking-calendar__week-dot${sessionDotClass(s) === 'full' ? ' booking-calendar__week-dot--full' : ''}`}
            />
          ));
        }}
        renderDayPanel={(date, daySessions, isPast, isClosed) => (
          <>
            <p className="booking-calendar__day-detail-heading">
              {formatDayDate(date)}
            </p>
            {isClosed && (
              <p className="booking-calendar__day-closed">
                Closed{getClosureForDate(date)?.reason ? `: ${getClosureForDate(date).reason}` : ''}
              </p>
            )}
            {!isClosed && daySessions.length === 0 && (
              <p className="booking-calendar__day-empty">No sessions</p>
            )}
            {!isClosed && (() => {
              const TYPE_ORDER = { TRAMPOLINE: 0, DMT: 1 };
              const sorted = [...daySessions].sort((a, b) => {
                const ta = TYPE_ORDER[a.type] ?? 2;
                const tb = TYPE_ORDER[b.type] ?? 2;
                return ta !== tb ? ta - tb : a.startTime.localeCompare(b.startTime);
              });
              const hasMultipleTypes = new Set(sorted.map(s => s.type)).size > 1;
              return sorted.map((s, idx) => {
                const prevType = idx > 0 ? sorted[idx - 1].type : null;
                const isNewGroup = hasMultipleTypes && idx > 0 && s.type !== prevType;
                return (
                  <React.Fragment key={s.id}>
                    {isNewGroup && (
                      <div style={{ borderTop: '1px solid var(--booking-border)', margin: '0.3rem 0' }} />
                    )}
                    <button
                      type="button"
                      className={`booking-calendar__day-session booking-calendar__day-session--${sessionDotClass(s)}`}
                      style={{ textDecoration: s.cancelledAt ? 'line-through' : 'none' }}
                      onClick={() => handleSelect(s.id)}
                    >
                      <span className="booking-calendar__day-session-time">{s.startTime} to {s.endTime}
                        <SessionTypePill session={s} />
                        {s.minAge && (
                          <span className="bk-pill bk-pill--info" style={{ marginLeft: '0.4rem' }}>{s.minAge}+</span>
                        )}
                      </span>
                      {/* Out of the session's places: a place held for a waitlist offer still counts */}
                      <span className="booking-calendar__day-session-status">{s.bookedCount}/{s.capacity ?? s.bookedCount + s.availableSlots}</span>
                    </button>
                  </React.Fragment>
                );
              });
            })()}
          </>
        )}
        renderMonthCell={(date, daySessions, isToday, isPast, isClosed) => (
          <>
            {isClosed && (
              <>
                <span className="booking-calendar__closed-label">Closed</span>
                {getClosureForDate(date)?.reason && (
                  <span className="booking-calendar__closed-reason">{getClosureForDate(date).reason}</span>
                )}
              </>
            )}
            {!isClosed && daySessions.map(s => (
              <div
                key={s.id}
                className={`booking-calendar__session booking-calendar__session--${sessionDotClass(s)}`}
                style={{ textDecoration: s.cancelledAt ? 'line-through' : 'none' }}
              >
                {s.startTime}
              </div>
            ))}
          </>
        )}
      />

      {/* The chosen session's details */}
      {selectedSession && (sessionDetail || detailFailed) && (
        <Sheet wide title={sessionDetail ? formatDayDate(sessionDetail.date) : 'Session'} onClose={closeDetail}>
          {detailFailed && !sessionDetail ? (
            <ErrorState title="Couldn't load this session" onRetry={() => setDetailTry(n => n + 1)} />
          ) : (
            <SessionDetailPanel
              sessionDetail={sessionDetail}
              selectedSession={selectedSession}
              showManualAdd={showManualAdd}
              setShowManualAdd={setShowManualAdd}
              onAdded={() => {
                setShowManualAdd(false);
                loadDetail(selectedSession);
                handleNavigate(lastNavigatedDate.current);
              }}
            />
          )}
        </Sheet>
      )}

    </div>
  );
}
